import type { AmortizationSystem, Installment, SimulationInput } from "../../shared/contracts";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 0
});

const detailedCurrencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function currency(value: number): string {
  return currencyFormatter.format(value);
}

export function currencyDetailed(value: number): string {
  return detailedCurrencyFormatter.format(value);
}

export function percent(value: number): string {
  return percentFormatter.format(value);
}

export function systemLabel(system: AmortizationSystem): string {
  return system === "sac" ? "SAC" : "Price";
}

export function termLabel(months: number): string {
  const years = Math.floor(months / 12);
  const remainder = months % 12;
  if (remainder === 0) return `${years} anos`;
  return `${years} anos e ${remainder} meses`;
}

export function scheduleToCsv(schedule: Installment[], annual = false): string {
  const headings = [
    annual ? "Ano" : "Mês",
    "Saldo inicial",
    "Amortização",
    "Juros",
    "Seguro",
    "Tarifa",
    "Prestação total",
    "Saldo final"
  ];
  const rows = schedule.map((item) => [
    item.month,
    item.openingBalance,
    item.amortization,
    item.interest,
    item.insurance,
    item.adminFee,
    item.totalPayment,
    item.closingBalance
  ]);
  return [headings, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(";"))
    .join("\n");
}

export function downloadText(filename: string, contents: string, type: string): void {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function buildShareUrl(input: SimulationInput): string {
  const url = new URL(window.location.origin);
  url.searchParams.set("imovel", String(input.propertyValue));
  url.searchParams.set("entrada", String(input.downPayment));
  url.searchParams.set("idade", String(input.age));
  url.searchParams.set("renda", String(input.monthlyIncome));
  url.searchParams.set("prazo", String(input.termMonths));
  return url.toString();
}

export function readShareInput(): SimulationInput | null {
  const query = new URLSearchParams(window.location.search);
  if (!query.has("imovel")) return null;
  const input = {
    propertyValue: Number(query.get("imovel")),
    downPayment: Number(query.get("entrada")),
    age: Number(query.get("idade")),
    monthlyIncome: Number(query.get("renda")),
    termMonths: Number(query.get("prazo"))
  };
  return Object.values(input).every(Number.isFinite) ? input : null;
}
